import React from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
  Legend
} from 'recharts';

const TrainingCurves = ({ trainingSummary, liveHistory, taskType = 'classification' }) => {
  // Normalize data from either source (prioritize trainingSummary over liveHistory)
  let data = [];
  let bestEpoch = null;
  let bestValLoss = null;
  let detectedTaskType = taskType;

  if (trainingSummary?.loss_history) {
    // Historical data from registry (preferred)
    const { loss_history = [], val_loss_history = [], val_metric_history = [] } = trainingSummary;
    detectedTaskType = trainingSummary.task_type || taskType;
    
    data = loss_history.map((loss, index) => ({
      epoch: index + 1,
      train_loss: loss,
      ...(val_loss_history[index] !== undefined && { val_loss: val_loss_history[index] }),
      ...(val_metric_history[index] !== undefined && {
        [detectedTaskType === 'classification' ? 'accuracy' : 'mse']: val_metric_history[index]
      })
    }));
    
    bestEpoch = trainingSummary.best_epoch;
    bestValLoss = trainingSummary.best_val_loss;
  } else if (liveHistory?.length > 0) {
    // Live data from polling (fallback)
    data = liveHistory.map((entry, index) => ({
      epoch: entry.epoch || index + 1,
      train_loss: entry.loss,
      ...(entry.val_loss != null && { val_loss: entry.val_loss }),
      ...(entry.accuracy != null && { accuracy: entry.accuracy }),
      ...(entry.mse != null && { mse: entry.mse })
    }));
  }

  if (data.length === 0) {
    return (
      <div className="training-curves training-curves--empty">
        <p>Waiting for training data...</p>
      </div>
    );
  }

  // Determine metric type
  const isClassification = detectedTaskType === 'classification' || data.some(d => d.accuracy !== undefined);
  const metricLabel = isClassification ? 'Accuracy' : 'MSE';
  const metricKey = isClassification ? 'accuracy' : 'mse';
  const hasValLoss = data.some(d => d.val_loss !== undefined);
  const hasMetric = data.some(d => d[metricKey] !== undefined);

  // Common styles
  const axisStyle = { fontSize: 11, fill: '#64748b' };
  const lineProps = { dot: false, activeDot: { r: 4, strokeWidth: 0 }, isAnimationActive: false };

  return (
    <div className="training-curves">
      <div className="training-curves__header">
        <div className="training-curves__title-group">
          <h3>Training Curves</h3>
          <span>Loss and {metricLabel} over epochs</span>
        </div>
        <div className="training-curves__badges">
          {bestEpoch && (
            <span className="training-curves__badge training-curves__badge--success">
              Best Epoch: {bestEpoch}
            </span>
          )}
          {bestValLoss != null && (
            <span className="training-curves__badge training-curves__badge--info">
              Min Val Loss: {bestValLoss.toFixed(4)}
            </span>
          )}
        </div>
      </div>

      <div className="training-curves__chart-container" style={{ height: '300px' }}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
            <XAxis 
              dataKey="epoch" 
              tick={axisStyle}
              label={{ value: 'Epoch', position: 'insideBottomRight', offset: -5, ...axisStyle }}
              tickLine={false}
              axisLine={{ stroke: '#e2e8f0' }}
            />
            <YAxis 
              yAxisId="loss"
              orientation="left"
              tick={axisStyle}
              label={{ value: 'Loss', angle: -90, position: 'insideLeft', ...axisStyle }}
              tickLine={false}
              axisLine={{ stroke: '#e2e8f0' }}
            />
            <YAxis 
              yAxisId="metric"
              orientation="right"
              tick={axisStyle}
              label={{ value: metricLabel, angle: 90, position: 'insideRight', ...axisStyle }}
              tickLine={false}
              axisLine={{ stroke: '#e2e8f0' }}
            />
            <Tooltip 
              contentStyle={{
                backgroundColor: '#fff',
                borderRadius: '8px',
                border: '1px solid #e2e8f0',
                fontSize: '12px',
                padding: '10px',
                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
              }}
              formatter={(value) => typeof value === 'number' ? value.toFixed(4) : value}
              labelFormatter={(label) => `Epoch ${label}`}
            />
            <Legend wrapperStyle={{ paddingTop: '10px' }} iconType="line" />
            
            <Line 
              yAxisId="loss"
              type="monotone" 
              dataKey="train_loss" 
              name="Train Loss"
              stroke="#3b82f6" 
              strokeWidth={2} 
              {...lineProps}
            />
            
            {hasValLoss && (
              <Line 
                yAxisId="loss"
                type="monotone" 
                dataKey="val_loss" 
                name="Val Loss"
                stroke="#ef4444" 
                strokeWidth={2} 
                {...lineProps}
              />
            )}
            
            {hasMetric && (
              <Line 
                yAxisId="metric"
                type="monotone" 
                dataKey={metricKey} 
                name={metricLabel}
                stroke="#10b981" 
                strokeWidth={2} 
                {...lineProps}
              />
            )}
            
            {bestEpoch && (
              <ReferenceLine 
                x={bestEpoch} 
                stroke="#10b981" 
                strokeDasharray="3 3" 
                strokeWidth={1.5}
                label={{ value: 'Best', position: 'top', fill: '#10b981', fontSize: 10 }}
              />
            )}
          </LineChart>
        </ResponsiveContainer>
      </div>
      <div className="visualization-context">
        <h4>Understanding Training Curve</h4>
          <ul>
            <li><strong>Training loss:</strong> Shows how well a model fits the training data</li>
            <li><strong>Validation loss:</strong> Measures ability to generalize on unseen data (validation set)</li>
            <li><strong>Early Stopping:</strong> Stops training when validation loss stops improving to prevent overfitting </li>
            <li><strong>Best Epoch:</strong> The epoch with the lowest validation loss, representing epoch with best generalization</li>
          </ul>
        <h4>Identifying Overfitting and Underfitting</h4>
          <ul>
            <li><strong>Overfitting:</strong> Low training loss but high validation loss. Model learns training data too well but fails to generalize.</li>
            <li><strong>Underfitting:</strong> Both training and validation losses are high. Model is too simple to capture underlying patterns.</li>
            <li><strong>Good Fit:</strong> Both training and validation losses decrease together, indicating effective learning and generalization.</li>
          </ul>
      </div>
    </div>
  );
};

export default TrainingCurves;
